import { useEffect } from 'react'

const SITE_URL = 'https://designflowdigitals.com'
const DEFAULT_OG_IMAGE =
  'https://cdn.magicpatterns.com/uploads/tEKY1KVFn8NPQae4HonbJW/9fea8157-abe6-4116-8e53-72149f139631.png'

interface SEOProps {
  title: string
  description: string
  /** Canonical path or absolute URL, e.g. "/seo-suite". Defaults to the current pathname. */
  canonical?: string
  /** Absolute image URL used for Open Graph / Twitter cards. */
  image?: string
  /** Comma-separated keywords for the meta keywords tag. */
  keywords?: string
  /** Open Graph type, e.g. "website", "product". Defaults to "website". */
  ogType?: string
  /** One or more JSON-LD structured-data objects for rich results. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
  /** Set to true to discourage indexing for this page. Defaults to indexable. */
  noindex?: boolean
}

function upsertMeta(
  selector: string,
  attr: 'name' | 'property',
  key: string,
  content: string,
) {
  let tag = document.querySelector(selector)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attr, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

export function usePageSEO({
  title,
  description,
  canonical,
  image = DEFAULT_OG_IMAGE,
  keywords,
  ogType = 'website',
  jsonLd,
  noindex = false,
}: SEOProps) {
  useEffect(() => {
    // Title
    document.title = title

    // Robots directive — give search and AI answer engines full permission
    // to index and to use full text/image snippets (important for GEO).
    const robotsContent = noindex
      ? 'noindex, nofollow'
      : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
    upsertMeta('meta[name="robots"]', 'name', 'robots', robotsContent)

    // Resolve canonical absolute URL
    const path = canonical
      ? canonical.startsWith('http')
        ? canonical
        : `${SITE_URL}${canonical.startsWith('/') ? '' : '/'}${canonical}`
      : `${SITE_URL}${window.location.pathname}`

    // Meta description
    upsertMeta('meta[name="description"]', 'name', 'description', description)

    // Keywords (optional)
    if (keywords) {
      upsertMeta('meta[name="keywords"]', 'name', 'keywords', keywords)
    }

    // Canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]')
    if (!canonicalLink) {
      canonicalLink = document.createElement('link')
      canonicalLink.setAttribute('rel', 'canonical')
      document.head.appendChild(canonicalLink)
    }
    canonicalLink.setAttribute('href', path)

    // Open Graph
    const ogTags: Record<string, string> = {
      'og:title': title,
      'og:description': description,
      'og:type': ogType,
      'og:site_name': 'Design Flow Digitals',
      'og:url': path,
      'og:image': image,
    }
    Object.entries(ogTags).forEach(([property, content]) => {
      upsertMeta(`meta[property="${property}"]`, 'property', property, content)
    })

    // Locale hint
    upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', 'en_US')

    // Twitter Card
    const twitterTags: Record<string, string> = {
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': image,
    }
    Object.entries(twitterTags).forEach(([name, content]) => {
      upsertMeta(`meta[name="${name}"]`, 'name', name, content)
    })

    // JSON-LD structured data — managed via a marked script tag we own
    const existing = document.getElementById('mp-jsonld')
    if (existing) existing.remove()
    if (jsonLd) {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.id = 'mp-jsonld'
      const payload = Array.isArray(jsonLd) ? jsonLd : [jsonLd]
      script.textContent = JSON.stringify(
        payload.length === 1 ? payload[0] : payload,
      )
      document.head.appendChild(script)
    }

    return () => {
      const node = document.getElementById('mp-jsonld')
      if (node) node.remove()
    }
  }, [
    title,
    description,
    canonical,
    image,
    keywords,
    ogType,
    noindex,
    JSON.stringify(jsonLd),
  ])
}
